import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACT_WEB_ACTIVITY_STATS } from '../../../lib/graphql/queries/contacts';

export interface ContactWebActivityStats {
  total: number;
  propertyViews: number;
  searches: number;
}

interface GetContactWebActivityStatsData {
  contact: {
    contactId: string;
    webActivityStats: ContactWebActivityStats;
  } | null;
}

const EMPTY_STATS: ContactWebActivityStats = { total: 0, propertyViews: 0, searches: 0 };

/**
 * Website activity counters for one contact — total events, property views and
 * searches — resolved by a single tenant-scoped aggregate on the backend rather
 * than by counting paginated event lists on the client.
 *
 * `eventTypes` narrows `total` to the types the caller presents, so the count
 * it shows reconciles with the events it renders instead of being inflated by
 * the page views and custom events no contact surface displays. It also scopes
 * `propertyViews` and `searches`, so keep those types in the set when their
 * counters are shown.
 */
export const useContactWebActivityStatsGQL = (
  contactId: string,
  eventTypes?: readonly string[]
) => {
  const { organization } = useOrganizationStore();

  const { data, loading, error } = useQuery<GetContactWebActivityStatsData>(
    GET_CONTACT_WEB_ACTIVITY_STATS,
    {
      variables: {
        organizationId: organization?.id ?? '',
        contactId,
        // An empty array reads as "no filter" on the backend, so send nothing
        // rather than a set that would silently widen the count.
        ...(eventTypes?.length ? { eventTypes: [...eventTypes] } : {}),
      },
      skip: !organization?.id || !contactId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const statsData = data?.contact?.webActivityStats;

  // `cache-and-network` re-reports loading on every background refetch, so
  // only the first load has nothing to show. Callers render a placeholder
  // for that pass and keep the last counters visible through a refresh.
  return { stats: statsData ?? EMPTY_STATS, loading: loading && !statsData, error };
};
